(() => {
  'use strict';

  const CONFIG = window.UnbegunMenuConfig || { featured: null, badges: {} };
  const I18N = window.UnbegunMenuI18n || {};
  const LOCALE_SEGMENT = /^[a-z]{2}(?:-[a-z]{2})?$/i;

  const normalizePath = (value) => {
    if (!value) return '';

    let pathname = '';
    try {
      pathname = new URL(value, window.location.origin).pathname;
    } catch (error) {
      pathname = String(value).split('?')[0].split('#')[0];
    }

    const parts = pathname.split('/').filter(Boolean);

    // Shopify Markets can prefix paths with /en, /de, /fr, /nl, /en-gb, etc.
    // Strip that locale segment so one config path works in every language.
    if (parts.length && LOCALE_SEGMENT.test(parts[0])) parts.shift();

    const normalized = `/${parts.join('/')}`.replace(/\/$/, '');
    return normalized || '/';
  };

  const matchesPath = (anchor, configuredPath) => {
    if (!anchor || !configuredPath) return false;
    return normalizePath(anchor.href) === normalizePath(configuredPath);
  };

  const badgeText = (type) => I18N[type] || type || '';

  const removeLegacyFeaturedPrefix = (anchor) => {
    if (!anchor || anchor.dataset.ubFeaturedTitleCleaned === 'true') return;

    // Horizon uses slightly different nested markup on desktop and mobile.
    // Walk all title text nodes, while leaving our labels, badges and icons intact.
    const prefixPattern = /^\s*(?:new|nieuw|neu|nouveau)\s*:\s*/i;
    const walker = document.createTreeWalker(anchor, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const parent = node.parentElement;
        if (!parent) return NodeFilter.FILTER_REJECT;
        if (parent.closest('.ub-menu-featured-label, .ub-menu-badge, svg, button')) {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      }
    });

    let node = walker.nextNode();
    while (node) {
      const value = node.textContent || '';
      if (prefixPattern.test(value)) {
        node.textContent = value.replace(prefixPattern, '');
        break;
      }
      node = walker.nextNode();
    }

    anchor.dataset.ubFeaturedTitleCleaned = 'true';
  };

  const addBadge = (anchor, type) => {
    if (!anchor || !type || anchor.querySelector('.ub-menu-badge')) return;
    const badge = document.createElement('span');
    badge.className = `ub-menu-badge ub-menu-badge--${type}`;
    badge.textContent = badgeText(type);
    badge.setAttribute('aria-hidden', 'true');
    anchor.appendChild(badge);
  };

  const markCategory = (item, anchor) => {
    if (!item || !anchor) return;
    if (anchor.querySelector('b, strong')) item.classList.add('ub-menu-category');
  };

  const markFeatured = (list, item, anchor, type) => {
    if (!list || !item || !anchor) return;

    item.classList.add('ub-menu-featured');
    removeLegacyFeaturedPrefix(anchor);

    if (!anchor.querySelector('.ub-menu-featured-label')) {
      const label = document.createElement('span');
      label.className = 'ub-menu-featured-label';
      label.textContent = badgeText(type || 'new');
      label.setAttribute('aria-hidden', 'true');
      anchor.prepend(label);
    }

    if (list.firstElementChild !== item) list.prepend(item);
  };

  const decorateList = (list, itemSelector, anchorSelector) => {
    if (!list) return;

    const items = Array.from(list.querySelectorAll(`:scope > ${itemSelector}`));
    items.forEach((item) => {
      const anchor = item.querySelector(`:scope > ${anchorSelector}`);
      if (!anchor) return;

      markCategory(item, anchor);

      const featured = CONFIG.featured;
      if (featured && matchesPath(anchor, featured.path)) {
        markFeatured(list, item, anchor, featured.badge || 'new');
        return;
      }

      Object.entries(CONFIG.badges || {}).some(([path, type]) => {
        if (!matchesPath(anchor, path)) return false;
        addBadge(anchor, type);
        return true;
      });
    });

    list.dataset.ubMenuDecorated = 'true';
  };

  const decorateDesktop = () => {
    document.querySelectorAll('.header__nav .nav__submenu').forEach((submenu) => {
      submenu.classList.add('ub-premium-nav');
      decorateList(submenu, 'li.nav__item', 'a.nav__link');
    });
  };

  const decorateMobile = () => {
    document.querySelectorAll('.drawer-menu .drawer-menu__list--sub').forEach((list) => {
      decorateList(list, 'li.drawer-menu__item:not(.drawer-menu__item--heading)', 'a.drawer-menu__link');
    });
  };

  let scheduled = false;
  const init = () => {
    scheduled = false;
    decorateDesktop();
    decorateMobile();
  };

  const scheduleInit = () => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Horizon may create or replace the mobile drawer after initial page load.
  // Re-apply only our isolated decoration when relevant DOM changes occur.
  const observer = new MutationObserver((mutations) => {
    const relevant = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some((node) =>
        node.nodeType === Node.ELEMENT_NODE &&
        (node.matches?.('.drawer-menu, .drawer-menu__list--sub, .nav__submenu') ||
          node.querySelector?.('.drawer-menu, .drawer-menu__list--sub, .nav__submenu'))
      )
    );
    if (relevant) scheduleInit();
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
